import type { QuoteServiceId } from '@/types'

export type FormQuestion = {
    id: string
    label: string
    help?: string
    type: 'choice' | 'multi' | 'text'
    options?: string[]
    optional?: boolean
    showIf?: { question: string, answers: string[] }
}

export type FormService = {
    id: QuoteServiceId
    label: string
    slugs: string[]
    questions: FormQuestion[]
    photoLabel: string
    urgency: {
        label: string
        options: string[]
        preset?: string
        defaultAnswer?: string
    }
    askCompany?: boolean
}

const URGENCY_OPTIONS = ["Today", "This week", "Flexible"]

const doorSize: FormQuestion = {
    id: 'door_size',
    label: "Roughly how wide is the door?",
    type: 'choice',
    options: ["Single (about 9 ft wide)", "Double (about 16 ft wide)", "Other / not sure"]
}

export const formServices: FormService[] = [
    {
        id: 'spring',
        label: "Spring replacement",
        slugs: ['broken-spring-repair'],
        questions: [
            {
                id: 'spring_type',
                label: "Which type of spring is it?",
                help: "Torsion springs wrap around a bar just above the door. Extension springs stretch along each track.",
                type: 'choice',
                options: ["Torsion (above the door)", "Extension (along the tracks)", "Not sure"]
            },
            doorSize
        ],
        photoLabel: "A photo of the spring helps (optional)",
        urgency: { label: "How soon do you need us?", options: URGENCY_OPTIONS, defaultAnswer: "Today" }
    },
    {
        id: 'opener',
        label: "Opener repair & installation",
        slugs: ['openers-smart-garage'],
        questions: [
            {
                id: 'opener_need',
                label: "What can we help with?",
                type: 'choice',
                options: ["Repair my opener", "Replace my opener", "Install one (I don't have one)"]
            },
            {
                id: 'opener_type',
                label: "Which opener is on the ceiling now?",
                type: 'choice',
                options: ["Chain drive", "Belt drive", "Screw drive", "Wall-mount", "Not sure"],
                showIf: { question: 'opener_need', answers: ["Repair my opener", "Replace my opener"] }
            },
            {
                id: 'brand',
                label: "Who made the opener?",
                help: "Look for the sticker on the motor unit. No sticker? Skip this one.",
                type: 'choice',
                optional: true,
                options: ["LiftMaster", "Chamberlain", "Genie", "Craftsman", "Other", "Not sure"],
                showIf: { question: 'opener_need', answers: ["Repair my opener", "Replace my opener"] }
            },
            {
                id: 'smart_features',
                label: "Anything you'd like it to do?",
                type: 'multi',
                optional: true,
                options: ["App control (Wi-Fi)", "Battery backup", "Built-in camera", "Keypad", "In-garage delivery", "None of these"],
                showIf: { question: 'opener_need', answers: ["Replace my opener", "Install one (I don't have one)"] }
            }
        ],
        photoLabel: "A photo of the opener helps (optional)",
        urgency: { label: "How soon do you need us?", options: URGENCY_OPTIONS }
    },
    {
        id: 'offtrack',
        label: "Off-track & cable repair",
        slugs: ['off-track-cable-repair'],
        questions: [doorSize],
        photoLabel: "A photo of the door helps (optional)",
        urgency: { label: "How soon do you need us?", options: URGENCY_OPTIONS, defaultAnswer: "Today" }
    },
    {
        id: 'panel',
        label: "Panel replacement",
        slugs: ['panel-replacement'],
        questions: [
            {
                id: 'panels_damaged',
                label: "How many sections are damaged?",
                type: 'choice',
                options: ["One", "Two", "Three or more"]
            },
            {
                id: 'brand',
                label: "Who made the door?",
                help: "The maker's sticker is usually on the inside of the door. No sticker? Skip this one.",
                type: 'choice',
                optional: true,
                options: ["Clopay", "Amarr", "Wayne Dalton", "CHI", "Other", "Not sure"]
            }
        ],
        photoLabel: "A photo of the damage (recommended, it speeds up the price)",
        urgency: { label: "How soon do you need us?", options: URGENCY_OPTIONS, defaultAnswer: "This week" }
    },
    {
        id: 'new-door',
        label: "New garage door",
        slugs: ['new-doors', 'new-garage-doors'],
        questions: [
            doorSize,
            {
                id: 'door_type',
                label: "Which style are you drawn to?",
                type: 'choice',
                options: ["Traditional raised panel", "Carriage house", "Modern / flush", "Full-view glass", "Not sure yet"]
            }
        ],
        photoLabel: "A photo of your current door helps (optional)",
        urgency: { label: "How soon do you need us?", options: URGENCY_OPTIONS, preset: "Flexible" }
    },
    {
        id: 'commercial',
        label: "Commercial doors",
        slugs: ['commercial-doors'],
        questions: [
            {
                id: 'door_type',
                label: "Which kind of commercial door is it?",
                type: 'choice',
                options: ["Rolling steel", "Sectional", "High-speed", "Dock door or leveler", "Fire door", "Not sure"]
            },
            {
                id: 'door_size',
                label: "Roughly how wide is it?",
                type: 'choice',
                options: ["Up to 10 ft", "10–20 ft", "Over 20 ft", "Not sure"]
            }
        ],
        photoLabel: "A photo of the door helps (optional)",
        urgency: { label: "Is the door holding up your work?", options: ["Yes, today", "This week", "Planned work"] },
        askCompany: true
    },
    {
        id: 'emergency',
        label: "Emergency repair",
        slugs: ['emergency-repair'],
        questions: [
            {
                id: 'emergency_type',
                label: "What's going on right now?",
                type: 'choice',
                options: ["Broken spring", "Door off track", "Door stuck open", "Car trapped inside", "Door hit by a vehicle", "Something else"]
            }
        ],
        photoLabel: "A photo helps (optional)",
        urgency: { label: "How soon do you need us?", options: URGENCY_OPTIONS, preset: "Today" }
    },
    {
        id: 'tune-up',
        label: "Tune-up & maintenance",
        slugs: ['tune-up-maintenance'],
        questions: [doorSize],
        photoLabel: "A photo helps (optional)",
        urgency: { label: "How soon do you need us?", options: URGENCY_OPTIONS, preset: "Flexible" }
    },
    {
        id: 'diagnosis-visit',
        label: "Not sure what's wrong",
        slugs: ['not-sure'],
        questions: [
            {
                id: 'symptoms',
                label: "Tell us what the door is doing",
                help: "A sentence or two is plenty: any noises, what moves and what stays put.",
                type: 'text',
                optional: true
            }
        ],
        photoLabel: "A photo helps (optional)",
        urgency: { label: "How soon do you need us?", options: URGENCY_OPTIONS }
    }
]

export const getFormService = (id: string): FormService | undefined =>
    formServices.find(service => service.id === id)

export const getFormServiceBySlug = (slug: string): FormService | undefined =>
    formServices.find(service => service.slugs.includes(slug) || service.id === slug)

export const VISIT_SLOTS = ["8–10 AM", "10 AM–12 PM", "12–2 PM", "2–4 PM", "4–6 PM"]

export const QUOTE_LIST_LIMIT = 4
